// Step 3: Contract Signature Component — Firma manuscrita digital
// Incluye pad de firma para todos los firmantes disponibles en el contrato

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ExclamationCircleIcon, PencilIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { SignaturePad } from '@/components/mandate/SignaturePad';
import type { HiringDetails } from '@/types/hiring';
import { hiringProgressService } from '@/services/hiringProgressService';

interface Signer {
  id: string;
  name: string;
  label: string;
}

interface ContractSignatureProps {
  hiringCode: string;
  userName: string;
  isAlreadySigned?: boolean;
  /** Datos completos del contrato para detectar múltiples firmantes */
  details?: HiringDetails;
  /** Callback con objeto de firmas: { [signerId]: base64 } */
  onComplete: (signature?: string, allSignatures?: Record<string, string>) => void;
  onBack: () => void;
}

/** Helper to translate signer labels and fallback names at render time */
function translateSigner(signer: Signer, t: (key: string) => string): Signer {
  const labelMap: Record<string, string> = {
    primarySigner: t('signature.primarySigner'),
    secondSigner: t('signature.secondSigner'),
  };
  const nameMap: Record<string, string> = {
    secondHolder: t('signature.secondHolder'),
  };
  return {
    ...signer,
    label: labelMap[signer.label] ?? signer.label,
    name: nameMap[signer.name] ?? signer.name,
  };
}

/**
 * Extrae la lista de firmantes del contrato.
 * - Individual / linked: 1 firmante (EL CLIENTE)
 * - Duo / family: 2 firmantes (titular principal + segundo titular)
 */
function getSigners(details?: HiringDetails, fallbackName?: string): Signer[] {
  const primaryName = details?.client_name || fallbackName || '';
  const signers: Signer[] = [
    { id: 'primary', name: primaryName, label: 'primarySigner' },
  ];

  const contractType = details?.contract_type || 'individual';
  const isMultiHolder = contractType === 'duo' || contractType === 'family';

  if (isMultiHolder) {
    let secondName = '';

    // Intentar obtener del supplementary_holder
    if (details?.supplementary_holder?.client_name) {
      secondName = details.supplementary_holder.client_name;
    }
    // Fallback: buscar en holders el no-primary (is_primary === false) o el segundo del array si no hay flag
    else if (details?.holders?.length) {
      const secondHolder =
        details.holders.find(h => h.is_primary === false) ??
        (details.holders.length >= 2 ? details.holders[1] : undefined);
      if (secondHolder?.full_name) {
        secondName = secondHolder.full_name;
      }
    }

    signers.push({
      id: 'secondary',
      name: secondName || 'secondHolder',
      label: 'secondSigner',
    });
  }

  return signers;
}

export function ContractSignature({
  hiringCode,
  userName,
  isAlreadySigned = false,
  details,
  onComplete,
  onBack,
}: ContractSignatureProps) {
  const { t } = useTranslation('hiring');
  const signers = useMemo(
    () => getSigners(details, userName).map(s => translateSigner(s, t)),
    [details, userName, t]
  );
  const isMultiStep = signers.length > 1;
  const signaturesStorageKey = useMemo(() => `contract_signatures_${hiringCode}`, [hiringCode]);

  // Pantalla actual: 0 = primer firmante, 1 = segundo firmante (solo si hay 2)
  const [signStep, setSignStep] = useState(0);

  // Estado: firma base64 por cada firmante
  const [signatures, setSignatures] = useState<Record<string, string | null>>(() => {
    const init: Record<string, string | null> = {};
    signers.forEach(s => { init[s.id] = null; });
    try {
      const raw = localStorage.getItem(`contract_signatures_${hiringCode}`);
      if (raw) {
        const persisted = JSON.parse(raw) as Record<string, string>;
        signers.forEach((s) => {
          if (persisted[s.id]) {
            init[s.id] = persisted[s.id];
          }
        });
      }
    } catch {
      // noop: si está corrupto, continuamos con estado vacío.
    }
    return init;
  });

  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const persistSignatureProgress = useCallback(
    (nextSignatures: Record<string, string | null>, nextAccepted: boolean) => {
      const signersProgress = signers.map((s) => {
        const signature = nextSignatures[s.id] ?? null;
        return {
          id: s.id,
          name: s.name,
          signed: Boolean(signature),
          signed_at: signature ? new Date().toISOString() : undefined,
          signature_base64: signature || undefined,
        };
      });
      const completed = signersProgress.every((s) => s.signed) && nextAccepted;
      void hiringProgressService.saveProgress(hiringCode, {
        step: 3,
        signatures: {
          accepted_terms: nextAccepted,
          signers: signersProgress,
          completed,
          completed_at: completed ? new Date().toISOString() : undefined,
        },
      });
    },
    [hiringCode, signers]
  );

  useEffect(() => {
    let mounted = true;
    const loadProgress = async () => {
      const progress = await hiringProgressService.getProgress(hiringCode);
      if (!mounted || !progress?.signatures) return;

      const restoredSignatures: Record<string, string | null> = {};
      signers.forEach((s) => {
        const signerProgress = progress.signatures?.signers?.find((sp) => sp.id === s.id);
        restoredSignatures[s.id] = signerProgress?.signature_base64 || null;
      });

      if (Object.values(restoredSignatures).some(Boolean)) {
        setSignatures((prev) => ({ ...prev, ...restoredSignatures }));
        try {
          const toStore: Record<string, string> = {};
          Object.entries(restoredSignatures).forEach(([key, value]) => {
            if (value) toStore[key] = value;
          });
          if (Object.keys(toStore).length > 0) {
            localStorage.setItem(signaturesStorageKey, JSON.stringify(toStore));
          }
        } catch {
          // noop
        }
      }

      if (progress.signatures.accepted_terms) {
        setAccepted(true);
      }
    };
    void loadProgress();
    return () => {
      mounted = false;
    };
  }, [hiringCode, signers, signaturesStorageKey]);

  // Firmante actual según el paso (una pantalla por firmante)
  const currentSigner = signers[signStep];
  const currentSignatureDone = currentSigner != null && signatures[currentSigner.id] != null;

  // Handler para cambio de firma de un firmante
  const handleSignatureChange = useCallback((signerId: string, base64: string | null) => {
    setSignatures(prev => {
      const next = { ...prev, [signerId]: base64 };
      try {
        const onlySigned: Record<string, string> = {};
        Object.entries(next).forEach(([key, value]) => {
          if (value) {
            onlySigned[key] = value;
          }
        });
        localStorage.setItem(signaturesStorageKey, JSON.stringify(onlySigned));
      } catch {
        // noop
      }
      persistSignatureProgress(next, accepted);
      return next;
    });
    setError(null);
  }, [accepted, persistSignatureProgress, signaturesStorageKey]);

  // Avanzar a la pantalla del siguiente firmante (solo multi-step)
  const handleNextSigner = () => {
    setError(null);
    if (signStep < signers.length - 1) {
      setSignStep(prev => prev + 1);
      void hiringProgressService.saveProgress(hiringCode, { step: 3 });
    }
  };

  // Volver a la pantalla del firmante anterior (multi-step) o al paso anterior del wizard
  const handleBackFromStep = () => {
    setError(null);
    if (isMultiStep && signStep > 0) {
      setSignStep(prev => prev - 1);
    } else {
      onBack();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validar que todos hayan firmado
    const unsigned = signers.filter(s => !signatures[s.id]);
    if (unsigned.length > 0) {
      setError(
        unsigned.length === 1
          ? t('signature.missingSignature', { name: unsigned[0].name })
          : t('signature.missingSignatures', { names: unsigned.map(s => s.name).join(', ') })
      );
      return;
    }

    // Validar que se acepten los términos
    if (!accepted) {
      setError(t('signature.mustAcceptTerms'));
      return;
    }

    // Construir objeto de firmas (solo las válidas)
    const allSignatures: Record<string, string> = {};
    signers.forEach(s => {
      if (signatures[s.id]) {
        allSignatures[s.id] = signatures[s.id]!;
      }
    });

    // Guardar firmas en localStorage
    localStorage.setItem(signaturesStorageKey, JSON.stringify(allSignatures));

    // Compatibilidad: guardar firma del principal como texto de nombre para el flujo legacy
    localStorage.setItem(`client_signature_${hiringCode}`, userName);

    // Llamar onComplete con la firma principal y todas las firmas
    persistSignatureProgress(signatures, true);
    onComplete(userName, allSignatures);
  };

  // Si ya está firmado
  if (isAlreadySigned) {
    return (
      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        <Card className="shadow-lg border-2 border-green-300 bg-green-50">
          <CardHeader className="bg-gradient-to-r from-green-100 to-green-50">
            <CardTitle className="text-xl sm:text-2xl text-green-900 flex items-center gap-2">
              <CheckCircleIcon className="text-green-600 flex-shrink-0" width={28} height={28} />
              {t('signature.alreadySigned')}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-6">
            <div className="bg-white border border-green-200 rounded-lg p-4 sm:p-6">
              <p className="text-base sm:text-lg text-green-900 mb-4">
                {t('signature.alreadySignedDesc')}
              </p>
              <p className="text-sm text-green-700">
                {t('signature.alreadySignedNote')}
              </p>
            </div>
            <div className="flex gap-4">
              <Button onClick={onBack} variant="outline" className="flex-1 min-h-[44px]">
                {t('common:buttons.back')}
              </Button>
              <Button
                onClick={() => onComplete()}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white min-h-[44px]"
              >
                {t('common:buttons.continue')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6">
      <Card className="shadow-lg">
        <CardHeader className="bg-gradient-to-r from-primary/10 to-secondary/10">
          <CardTitle className="text-xl sm:text-2xl text-emphasis-900 flex items-center gap-2">
            <PencilIcon className="text-primary flex-shrink-0" width={28} height={28} />
            {t('signature.title')}
          </CardTitle>
        </CardHeader>

        <CardContent className="pt-6 space-y-6">
          {/* Declaración de aceptación: solo en la primera pantalla */}
          {signStep === 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-blue-900 mb-3">
                {t('signature.declarationTitle')}
              </h3>
              <p className="text-blue-800 mb-4 text-sm sm:text-base">
                {t('signature.declarationIntro')}
              </p>
              <ul className="space-y-2 text-sm text-blue-800 ml-4">
                <li>{t('signature.declarationItem1')}</li>
                <li>{t('signature.declarationItem2')}</li>
                <li>{t('signature.declarationItem3')}</li>
                <li>{t('signature.declarationItem4')}</li>
              </ul>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Una pantalla por firmante: solo el pad del firmante actual */}
            {currentSigner && (
              <div className="space-y-3">
                {/* Indicador de paso (ej. "1 de 2") */}
                {isMultiStep && (
                  <p className="text-sm text-gray-500 font-medium">
                    {t('signature.signerProgress', { step: signStep + 1, total: signers.length })}
                  </p>
                )}
                {/* Encabezado del firmante */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">
                        <strong>{currentSigner.label}</strong>
                      </p>
                      <p className="text-lg font-semibold text-emphasis-900">
                        {currentSigner.name}
                      </p>
                    </div>
                    {signatures[currentSigner.id] && (
                      <CheckCircleIcon className="text-green-600 flex-shrink-0" width={24} height={24} />
                    )}
                  </div>
                </div>

                {/* Pad de firma del firmante actual */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    {t('signature.signatureLabel', { name: currentSigner.name })}
                  </label>
                  <p className="text-sm text-gray-500 mb-3">
                    {t('signature.signatureHint')}
                  </p>
                  <SignaturePad
                    key={currentSigner.id}
                    onSignatureChange={(base64) => handleSignatureChange(currentSigner.id, base64)}
                    height={200}
                    hintText={t('signature.signHere', { name: currentSigner.name })}
                    drawHintText={t('signature.drawHint')}
                    undoLabel={t('signature.undo')}
                    clearLabel={t('signature.clear')}
                    noSignatureLabel={t('signature.noSignature')}
                    validSignatureLabel={t('signature.signatureValidShort')}
                    shortSignatureLabel={t('signature.signatureTooShort')}
                    rotateHintText={t('signature.rotateHint')}
                    disabled={false}
                  />
                </div>
              </div>
            )}

            {/* Aceptación de términos: en la última pantalla (único firmante o segundo en duo) */}
            {(signStep === signers.length - 1) && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={accepted}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setAccepted(checked);
                      persistSignatureProgress(signatures, checked);
                      setError(null);
                    }}
                    className="mt-1 w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary flex-shrink-0"
                  />
                  <span className="text-sm text-gray-700">
                    {t('signature.acceptTerms', { names: signers.map(s => s.name).join(' y ') })}
                  </span>
                </label>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg flex items-start gap-2">
                <ExclamationCircleIcon width={20} height={20} className="mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold">{t('signature.validationError')}</p>
                  <p className="text-sm">{error}</p>
                </div>
              </div>
            )}

            {/* Estado de firma actual (última pantalla) */}
            {signStep === signers.length - 1 && currentSignatureDone && accepted && (
              <div className="bg-green-50 border border-green-200 text-green-800 p-4 rounded-lg flex items-start gap-2">
                <CheckCircleIcon width={20} height={20} className="mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold">
                    {signers.length === 1 ? t('signature.signatureValid') : t('signature.allSignaturesValid')}
                  </p>
                  <p className="text-sm">
                    {signers.length === 1
                      ? t('signature.signatureCaptured')
                      : t('signature.allSignaturesCaptured', { count: signers.length })}
                  </p>
                </div>
              </div>
            )}

            {/* Nota importante */}
            <div className="bg-gray-50 border-l-4 border-primary p-4 rounded">
              <p className="text-sm text-gray-700">
                <strong>{t('signature.importantNote')}</strong> {t('signature.importantNoteText')}
              </p>
            </div>

            {/* Botones según paso */}
            <div className="flex gap-4 pt-4">
              <Button
                type="button"
                onClick={handleBackFromStep}
                variant="outline"
                className="flex-1 min-h-[44px]"
              >
                {t('common:buttons.back')}
              </Button>
              {isMultiStep && signStep < signers.length - 1 ? (
                <Button
                  type="button"
                  onClick={handleNextSigner}
                  disabled={!currentSignatureDone}
                  className="flex-1 bg-primary hover:bg-primary-700 text-white disabled:bg-gray-300 disabled:cursor-not-allowed min-h-[44px]"
                >
                  {currentSignatureDone ? (
                    <>
                      {t('signature.nextSigner')}
                      <span className="ml-2">→</span>
                    </>
                  ) : (
                    t('signature.signToContinue')
                  )}
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={!currentSignatureDone || !accepted}
                  className="flex-1 bg-primary hover:bg-primary-700 text-white disabled:bg-gray-300 disabled:cursor-not-allowed min-h-[44px]"
                >
                  {currentSignatureDone && accepted ? (
                    <>
                      <CheckCircleIcon className="mr-2 flex-shrink-0" width={18} height={18} />
                      {t('signature.confirmSignature')}
                    </>
                  ) : (
                    t('signature.signContract')
                  )}
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
